import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { FlexColumn, Gap } from 'ui-components/components/flex';
import { INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { Input, version2 } from 'ui-components/components/input';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalFooterCancelButton } from 'ui-components/components/modal/modal-footer/cancel-button';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import styles from './add-blacklist-entry-modal.module.less';
import { messages } from './messages';
import { AddressFields } from '../../../../../../address/address-fields';
import { FIELD_NAMES } from '../../../../../../address/address-fields/constants';
import ModalPortal from '../../../../../../components/modal-portal';
import { addBlacklistEntrySchema } from '../../../../../../utils/blacklist-address-schema';
import { useYupFormValidation } from '../../../../../../validation/use-yup-form-validation';

/** @typedef {import('../../types').AddBlacklistEntryPayload} AddBlacklistEntryPayload */

// Fields to hide from AddressFields - we only need address fields, not contact info
const HIDDEN_FIELDS = [FIELD_NAMES.NAME, FIELD_NAMES.COMPANY, FIELD_NAMES.PHONE, FIELD_NAMES.EMAIL];

// Address fields that are required for blacklist entries
const REQUIRED_ADDRESS_FIELDS = [FIELD_NAMES.LINE1, FIELD_NAMES.CITY, FIELD_NAMES.STATE, FIELD_NAMES.POSTAL_CODE, FIELD_NAMES.COUNTRY_CODE];

const INITIAL_ADDRESS_STATE = {
  line1: '',
  line2: '',
  city: '',
  state: '',
  postalCode: '',
  countryCode: '',
};

/**
 * @param {object} props
 * @param {() => void} props.onClose - Handler for closing the modal
 * @param {(payload: AddBlacklistEntryPayload) => void} props.onAdd - Handler for adding the entry
 * @param {boolean} [props.isAdding] - Whether add is in progress
 * @returns {JSX.Element}
 */
export const AddBlacklistEntryModal = ({ onClose, onAdd, isAdding = false }) => {
  const intl = useIntl();
  const [address, setAddress] = useState(INITIAL_ADDRESS_STATE);
  const [reason, setReason] = useState('');
  const [showAllErrors, setShowAllErrors] = useState(false);

  const form = useMemo(
    () => ({
      ...address,
      reason,
    }),
    [address, reason],
  );

  const { errors, isValid } = useYupFormValidation({ form, schema: addBlacklistEntrySchema });

  const reasonError = errors.reason?.message;

  const handleAddressChange = useCallback((field, value) => {
    setAddress((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleReasonChange = useCallback((event) => {
    setReason(event.target.value);
  }, []);

  const handleAdd = useCallback(() => {
    if (!isValid) {
      setShowAllErrors(true);
      return;
    }

    // Map AddressFields field names to backend API field names
    const payload = {
      addressLine1: address.line1.trim(),
      addressLine2: address.line2?.trim() || null,
      city: address.city.trim(),
      state: address.state.trim(),
      postalCode: address.postalCode.trim(),
      countryCode: address.countryCode.trim(),
      reason: reason?.trim() || null,
    };
    onAdd(payload);
  }, [address, reason, isValid, onAdd]);

  return (
    <ModalPortal onClose={onClose}>
      <Modal size='medium'>
        <ModalHeader>
          <Text textType={TextType.H3} value={messages.header} />
        </ModalHeader>
        <ModalBody className={styles['modal-body']}>
          <FlexColumn gap={Gap.Space4}>
            <Text className={styles.description} textType={TextType.Description} value={messages.description} />

            <AddressFields
              additionalRequiredFields={REQUIRED_ADDRESS_FIELDS}
              address={address}
              hiddenFields={HIDDEN_FIELDS}
              isFormDescendant
              onChange={handleAddressChange}
              showAllErrors={showAllErrors}
              state
              type='v2'
            />

            <Input
              hasError={Boolean(reasonError)}
              inlineMessage={reasonError}
              inlineMessageType={reasonError ? INLINE_MESSAGE_TYPES.error : undefined}
              label={intl.formatMessage(messages.reasonLabel)}
              name='reason'
              onChange={handleReasonChange}
              placeholder={intl.formatMessage(messages.reasonPlaceholder)}
              value={reason}
              version={version2}
            />
          </FlexColumn>
        </ModalBody>
        <ModalFooter>
          <ModalFooterCancelButton disabled={isAdding} />
          <Button disabled={isAdding || !isValid} onClick={handleAdd} theme={THEME_CONSTANTS.primary}>
            <Text textType={TextType.Label} value={isAdding ? messages.adding : messages.add} />
          </Button>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};
